import { Route, Routes as ReactRouterRoutes } from "react-router-dom";
import Container from "@mui/material/Container";
import withTitle from "common/hocs/withTitle";
import { UnauthorizedPage } from "components/unauthrozied/UnauthorizedPage";
import { PostLogin } from "components/postLogin/PostLogin";
import { PostLogout } from "components/postLogout/PostLogout";
import { Routes } from "./routes";
import { withAdminCheck } from "./common/hocs/withAdminCheck";
import { lazy, Suspense } from "react";
import { LoadingSpinner } from "./components/common";
import { lazily } from "react-lazily";
import ProtectedRoute from "./components/common/protectedRoute/ProtectedRoute";
import { OdinAccess } from "./common/enums";
import CompanyPage from "./components/companyPage";
import { ScannedRecently } from "./components/freya/scannedRecently/ScannedRecently";
import { SentryUnmask } from "components/common/sentryUnmask/SentryUnmask";

const { GoToServiceDeskFail } = lazily(() => import("./components/freya/goToServiceDeskFail/GoToServiceDeskFail"));
const SearchPage = lazy(() => import("components/search"));
const CreateBadgePage = lazy(() => import("components/createBadge"));
const DashboardPage = lazy(() => import("components/dashboard"));
const { NotFoundPage } = lazily(() => import("components/notFound/NotFoundPage"));
const { PrintSettings } = lazily(() => import("./components/printSettings/PrintSettings"));
const DevicesPage = lazy(() => import("./components/devices"));
const SettingsPage = lazy(() => import("./components/globalSettings"));
const { FreyaSettingsRenderer } = lazily(() => import("./components/printSettings/freya/FreyaSettingsRenderer"));
const FreyaLogin = lazy(() => import("./components/freya/login/FreyaLogin"));
const { Landing: FreyaLanding } = lazily(() => import("./components/freya/landing/Landing"));
const { ScanYourQrCode: FreyaScanQrCode } = lazily(() => import("./components/freya/scanBadge/ScanYourQrCode"));
const { ScanSuccess: FreyaScanSuccess } = lazily(() => import("./components/freya/scanSuccess/ScanSuccess"));
const { ScanFailed: FreyaScanFailed } = lazily(() => import("./components/freya/scanFailed/ScanFailed"));
const MakePhotoScreen = lazy(() => import("./components/freya/makePhoto/MakePhotoPage"));
const BadgeDetails = lazy(() => import("components/search/details/badgeDetailsParamsWrapper/BadgeDetailsParamsWrapper"));
const ZoneScanningBreakdown = lazy(() => import("./components/dashboard/tables/ZoneScanningBreakdown/ZoneScanningBreakdown"));

const SearchScreen = withTitle({ component: SearchPage, title: "badges" });
const CreateBadgeScreen = withTitle({ component: CreateBadgePage, title: "createBadge" });
const DashboardScreen = withTitle({ component: DashboardPage, title: "dashboard" });
const NotFoundScreen = withTitle({ component: NotFoundPage, title: "notFound" });
const PrintSettingsScreen = withTitle({ component: PrintSettings, title: "printerSettings" });
const DevicesScreen = withTitle({ component: DevicesPage, title: "devices" });
const GlobalSettingsScreen = withTitle({ component: SettingsPage, title: "globalSettings" });
const CompanyPageScreen = withTitle({ component: CompanyPage, title: "company" });
const ZoneScanningBreakdownScreen = withTitle({ component: ZoneScanningBreakdown, title: "zoneScanning" });

const Routers = () => {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <ReactRouterRoutes>
        <Route path={`${Routes.freya}${Routes.printSettings}`} element={<FreyaSettingsRenderer />} />
        <Route path={Routes.freya} element={<FreyaLogin />} />
        <Route path={`${Routes.freya}${Routes.start}`} element={<FreyaLanding />} />
        <Route path={`${Routes.freya}${Routes.scanQrCode}`} element={<FreyaScanQrCode />} />
        <Route path={`${Routes.freya}${Routes.makePhoto}`} element={<MakePhotoScreen />} />
        <Route path={Routes.company} element={<Container maxWidth="xl" children={<CompanyPageScreen />} />} />
        <Route path={`${Routes.freya}${Routes.scanQrCode}${Routes.success}`} element={<FreyaScanSuccess />} />
        <Route path={`${Routes.freya}${Routes.scanQrCode}${Routes.failed}`} element={<FreyaScanFailed />} />
        <Route path={`${Routes.freya}${Routes.scanQrCode}${Routes.goToServiceDesk}`} element={<GoToServiceDeskFail />} />
        <Route path={`${Routes.freya}${Routes.scanQrCode}${Routes.scannedRecently}`} element={<ScannedRecently />} />
        <Route path={Routes.authpcke} element={<Container maxWidth="xl" children={<PostLogin />} />} />
        <Route path={Routes.unauthorized} element={<UnauthorizedPage />} />
        <Route path={Routes.badges} element={<SearchScreen />} />
        <Route path={Routes.createBadge} element={<ProtectedRoute accessKey={OdinAccess.CREATE_NEW_REGISTRATION} />}>
          <Route path={Routes.createBadge} element={<CreateBadgeScreen />} />
        </Route>
        <Route path={`${Routes.badges}/:id`} element={<BadgeDetails />} />
        <Route path={Routes.homepage} element={<ProtectedRoute accessKey={OdinAccess.SHOW_STATISTICS} />}>
          <Route
            path={Routes.homepage}
            element={
              <Container
                maxWidth="xl"
                children={
                  <SentryUnmask>
                    <DashboardScreen />
                  </SentryUnmask>
                }
              />
            }
          />
        </Route>
        <Route path={Routes.printSettings} element={<ProtectedRoute accessKey={OdinAccess.PRINTER_SETUP} />}>
          <Route path={Routes.printSettings} element={<PrintSettingsScreen showLogoSwitch />} />
        </Route>
        <Route path={Routes.globalSettings} element={<GlobalSettingsScreen />} />
        <Route path={Routes.logout} element={<Container maxWidth="xl" children={<PostLogout />} />} />
        <Route path={Routes.devices} element={<ProtectedRoute accessKey={OdinAccess.DEVICE_SETUP} />}>
          <Route
            path={Routes.devices}
            element={
              <SentryUnmask>
                <DevicesScreen />
              </SentryUnmask>
            }
          />
        </Route>
        <Route path={Routes.zoneScanningBreakdown} element={<Container maxWidth="xl" children={<ZoneScanningBreakdownScreen />} />} />
        <Route element={<NotFoundScreen />} />
      </ReactRouterRoutes>
    </Suspense>
  );
};

export default withAdminCheck(Routers);
